import {
  BrowserCacheLocation,
  InteractionType,
  IPublicClientApplication,
  LogLevel,
  PublicClientApplication,
  ResponseMode
} from "@azure/msal-browser";
import {
  MsalGuardConfiguration,
  MsalInterceptorConfiguration
} from "@azure/msal-angular";
import { ClientConfigService } from "@app/core/config/client-config.service";

/**
 * Logger callback for MSAL
 */
export function loggerCallback(logLevel: LogLevel, message: string) {
  console.log(logLevel, message);
}

/**
 * Factory function to create an MSAL instance
 */
export function MSALInstanceFactory(
  clientConfigService: ClientConfigService
): IPublicClientApplication {
  const config = clientConfigService.config;
  const msalConfig = {
    auth: {
      clientId: config.azureConfig.clientId,
      authority:
        "https://login.microsoftonline.com/" + config.azureConfig.tenantId,
      knownAuthorities: [
        "https://login.microsoftonline.com/" + config.azureConfig.tenantId
      ],
      redirectUri: config.azureConfig.publicUrl,
      postLogoutRedirectUri: config.azureConfig.publicUrl,
      // NOTE: In MSAL v5 navigateToLoginRequestUrl is NOT read from this auth
      // config (StandardController ignores it) — it only takes effect when passed
      // per-call to handleRedirectPromise. We pass `false` there (see
      // AuthService.handleRedirectResponse / AppComponent). This field is left as
      // documentation of intent and for forward/backward compatibility; on its
      // own it is inert in v5.
      navigateToLoginRequestUrl: false
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
      storeAuthStateInCookie: true,
      secureCookies: true, // Allow non-secure cookies for development
      claimsBasedCachingEnabled: true // Disable claims-based caching that can cause token storage issues
    },
    system: {
      allowRedirectInIframe: true, // Enable redirects in iframes for better compatibility
      loggerOptions: {
        loggerCallback,
        logLevel: LogLevel.Warning, // Increase logging level for troubleshooting
        piiLoggingEnabled: false
      },
      windowHashTimeout: 9000, // Increase timeout for handling redirect responses
      tokenRenewalOffsetSeconds: 5 // Default offset; 300 was causing premature silent refresh attempts
    }
  };

  console.log(
    "[MSALInstanceFactory] Creating MSAL instance with config:",
    JSON.stringify(msalConfig)
  );
  return new PublicClientApplication(msalConfig);
}

/**
 * Factory function to create MSAL interceptor configuration
 */
export function MSALInterceptorConfigFactory(
  clientConfigService: ClientConfigService
): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();

  // Add API endpoints from config
  clientConfigService.config.azureConfig.apiConfig.forEach((value) => {
    console.log(
      "Adding protected resource:",
      value.origin,
      "with scopes:",
      value.scopes
    );
    protectedResourceMap.set(value.origin + "/*", value.scopes);
  });

  // Add Microsoft Graph API if needed
  protectedResourceMap.set("https://graph.microsoft.com/v1.0/me", [
    "user.read"
  ]);

  console.log(
    "Protected resources configured:",
    Array.from(protectedResourceMap.entries())
  );

  return {
    interactionType: InteractionType.Redirect, // Using Redirect for authentication
    protectedResourceMap
  };
}

/**
 * Factory function to create MSAL guard configuration.
 *
 * The guard initiates loginRedirect for guarded deep links (bypassing
 * AuthService.login()), so it must carry the same responseMode — otherwise
 * deep-link logins use fragment while AuthService logins use the configured
 * mode, making the Chrome 149 comparison inconsistent.
 */
export function MSALGuardConfigFactory(
  clientConfigService: ClientConfigService
): MsalGuardConfiguration {
  const responseMode = clientConfigService.config.azureConfig.responseMode;
  return {
    interactionType: InteractionType.Redirect, // Using Redirect for authentication
    authRequest: {
      scopes: ["user.read"],
      ...(responseMode ? { responseMode: responseMode as ResponseMode } : {})
    }
  };
}
